/**
 * Canonical strategy keys for exit-strategy chat — must match the backend
 * `invokeExitStrategyChat` validator (see error payload "Valid values" list).
 */
export const VALID_STRATEGY_KEYS = new Set([
  "fix_and_flip",
  "fix_and_list",
  "brrrr",
  "wholesale",
  "short_term_rental",
  "house_hacking",
  "commercial_mixed_use",
  "buy_hold_long_term_rental",
  "padsplit",
  "adu_dadu",
  "adu_fix_and_flip",
  "livlyte",
  "splash_and_dash",
]);

export const DEFAULT_SELECTED_STRATEGIES = Object.freeze([
  "fix_and_flip",
  "brrrr",
  "adu_dadu",
  "fix_and_list",
]);

export const EXIT_STRATEGY_OPTIONS = Object.freeze([
  { key: "fix_and_flip", label: "Fix and Flip" },
  { key: "brrrr", label: "BRRRR" },
  { key: "adu_dadu", label: "ADU / DADU" },
  { key: "fix_and_list", label: "Fix and List" },
  { key: "splash_and_dash", label: "Splash and Dash" },
  { key: "livlyte", label: "Livlyte" },
  { key: "buy_hold_long_term_rental", label: "Buy and Hold" },
  { key: "wholesale", label: "Wholesale" },
  { key: "house_hacking", label: "House Hacking" },
  { key: "short_term_rental", label: "Short Term Rental" },
  { key: "padsplit", label: "Padsplit" },
  { key: "commercial_mixed_use", label: "Commercial Use" },
]);

/**
 * Frontend strategy_name / display aliases -> backend `strategy_key`.
 * Use `buy_hold_long_term_rental` (not `buy_and_hold`) for chat payloads.
 */
export const STRATEGY_KEY_MAP = {
  buy_hold_long_term_rental: "buy_hold_long_term_rental",
  buy_and_hold: "buy_hold_long_term_rental",
  livelyte: "livlyte",
  livlyte: "livlyte",
  "fix and flip": "fix_and_flip",
  fix_and_list: "fix_and_list",
  "fix and list": "fix_and_list",
  splash_and_dash: "splash_and_dash",
  "splash and dash": "splash_and_dash",
  "short term rental": "short_term_rental",
  "house hacking": "house_hacking",
  "buy and hold": "buy_hold_long_term_rental",
  commercial_mixed_use: "commercial_mixed_use",
  "commercial mixed use": "commercial_mixed_use",
  adu_dadu: "adu_dadu",
  "adu / dadu": "adu_dadu",
  "adu and dadu": "adu_dadu",
  "adu dadu": "adu_dadu",
  "adu_dadu": "adu_dadu",
  "adu fix and flip": "adu_dadu",
  adu_fix_and_flip: "adu_dadu",
};

/** Canonical `strategy_key` → card title override (product naming). */
const EXIT_STRATEGY_CARD_TITLE_OVERRIDES = {
  adu_dadu: "ADU/DADU",
  adu_fix_and_flip: "ADU/DADU",
  brrrr: "BRRRR",
};

/**
 * Human-readable card title: applies product overrides, then API display_name, then strategy_name.
 *
 * @param {string | null | undefined} strategyName - Machine id (e.g. `adu_fix_and_flip`)
 * @param {string | null | undefined} displayNameFromApi - Optional `display_name` from API
 * @returns {string}
 */
export function exitStrategyCardTitle(strategyName, displayNameFromApi) {
  const sn = typeof strategyName === "string" ? strategyName.trim() : "";
  const dn = typeof displayNameFromApi === "string" ? displayNameFromApi.trim() : "";
  const key = (sn && toStrategyKey(sn)) || (dn && toStrategyKey(dn)) || null;
  if (key && EXIT_STRATEGY_CARD_TITLE_OVERRIDES[key]) {
    return EXIT_STRATEGY_CARD_TITLE_OVERRIDES[key];
  }
  if (dn) return dn;
  if (sn) return sn;
  return "Strategy";
}

/**
 * @param {string} value
 * @returns {string}
 */
function normalizeStrategyToken(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * @param {string | null | undefined} strategyName
 * @returns {string | null}
 */
export function toStrategyKey(strategyName) {
  if (typeof strategyName !== "string") return null;
  const normalizedRaw = strategyName.trim();
  if (!normalizedRaw) return null;
  const normalizedToken = normalizeStrategyToken(normalizedRaw);
  if (!normalizedToken) return null;
  const mapped =
    STRATEGY_KEY_MAP[normalizedRaw] ??
    STRATEGY_KEY_MAP[normalizedRaw.toLowerCase()] ??
    STRATEGY_KEY_MAP[normalizedToken] ??
    normalizedToken;
  return VALID_STRATEGY_KEYS.has(mapped) ? mapped : null;
}

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
function dedupeValidStrategyKeys(raw) {
  if (!Array.isArray(raw)) return [];

  const seen = new Set();
  const keys = [];
  for (const item of raw) {
    const rawKey =
      item != null && typeof item === "object"
        ? (item.strategy_id ?? item.strategyId ?? item.key)
        : item;
    const key = toStrategyKey(rawKey);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
}

/**
 * Agent-recommended strategy keys from QMA `qma_added_strategies`.
 *
 * @param {Object|null|undefined} additionalAttributes
 * @returns {string[]}
 */
export function extractQmaRecommendedStrategyKeys(additionalAttributes) {
  if (!additionalAttributes || typeof additionalAttributes !== "object") return [];
  return dedupeValidStrategyKeys(additionalAttributes.qma_added_strategies);
}

/**
 * Resolves the default exit-strategy selection for a property.
 * Saved `selected_exit_strategies` wins; otherwise QMA recommendations; otherwise product defaults.
 *
 * @param {Object|null|undefined} additionalAttributes
 * @returns {string[]}
 */
export function resolveSelectedExitStrategies(additionalAttributes) {
  const attrs = additionalAttributes && typeof additionalAttributes === "object" ? additionalAttributes : null;
  const rawSaved = attrs?.selected_exit_strategies;

  if (Array.isArray(rawSaved)) {
    const seen = new Set();
    const validSelection = [];
    for (const item of rawSaved) {
      const key = toStrategyKey(item);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      validSelection.push(key);
    }
    if (validSelection.length > 0) return validSelection;
  }

  const qmaRecommended = extractQmaRecommendedStrategyKeys(attrs);
  if (qmaRecommended.length > 0) return qmaRecommended;

  return [...DEFAULT_SELECTED_STRATEGIES];
}

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
export function sanitizeSelectedStrategies(raw) {
  if (!Array.isArray(raw)) {
    return [...DEFAULT_SELECTED_STRATEGIES];
  }

  const seen = new Set();
  const validSelection = [];
  for (const item of raw) {
    const key = toStrategyKey(item);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    validSelection.push(key);
  }

  return validSelection.length > 0 ? validSelection : [...DEFAULT_SELECTED_STRATEGIES];
}
